import { Box, Button, Container, Divider, LinearProgress, OutlinedInput, Paper, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { useCallback, useEffect, useState } from "react";
import { assetService, cacheService, setupService } from "../../../../../Core/Api/services";
import { AclMatch, UserHasAcls } from "../../../../../Utils/helpers";
import { AclTypes } from "../../../../../Core/Api/Types/acl-types";
import { AssetPanelDto, DbAssetObjectInfos } from "../../../../../Core/Api/Types/db-asset-object-infos";
import { DbQuotaInfos } from "../../../../../Core/Api/Types/db-quota-infos";
import { QuotaRulesEnum } from "../../../../../Core/Api/Types/quota-rules-enum";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import ClearIcon from "@mui/icons-material/Clear";
import { AssetObjectTreeStorageSizeQuota } from "../../../../../Core/Api/Types/i-quota-base-rule";
import useCustomSnackbar from "../../../../../Utils/Hooks/useCustomSnackbar";
import { useSelector } from "react-redux";
import { GetObject } from "../../../../../Redux/Reducers/redux-slice-asset";
import CircularProgress, { CircularProgressProps } from "@mui/material/CircularProgress";
import TableSkeleton from "../../../../../Components/Table/TableSkeleton";
import { useTranslation } from "react-i18next";

export type QuotaConfigProps = {
  asset?: DbAssetObjectInfos | undefined;
};

export const QuotaConfig: React.FC<QuotaConfigProps> = (props) => {
  const { t } = useTranslation();
  const { asset } = props;
  const enqueueCustomSnackbar = useCustomSnackbar();
  const [value, setValue] = useState<number | null>(null);
  const [quota, setQuota] = useState<DbQuotaInfos | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [trigger, setTrigger] = useState<boolean>(false);
  const aclUpdate = UserHasAcls([AclTypes.aclAdmin, AclTypes.aclSuperUser], AclMatch.OneAtLeast);

  useEffect(() => {
    if (!asset) return;

    assetService.GetQuotas(asset?.id).then((response) => {
      let quota = response.quotas?.find((x) => x.type === QuotaRulesEnum.AssetObjectTreeStorageSizeQuota);
      setQuota(quota);
      setIsLoading(false);
    });
  }, [asset, trigger]);

  const handleQuotaCreate = useCallback(() => {
    if (!asset || !value) return;

    let quotaObj: DbQuotaInfos = {
      platformId: cacheService.GetUserInfo()!.platformId,
      resourceId: asset.id,
      enabled: true,
      creationDate: new Date(),
      description: null,
      type: QuotaRulesEnum.AssetObjectTreeStorageSizeQuota,
      rule: new AssetObjectTreeStorageSizeQuota({ maxSizeInBytes: value * 1024 * 1024 * 1024 }),
      report: null,
      id: 0,
      isNew: true,
    };

    setupService.CreateQuota(quotaObj).then((response) => {
      if (response.success) {
        enqueueCustomSnackbar(t("alert:success.quotaEnabled"), "success");
        quotaObj.id = response.objectId!;
        setValue(0);
        setIsLoading(true);
        setTrigger((prevState) => !prevState);
      } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
    });
  }, [asset, value]);

  const handleQuotaUpdate = useCallback(() => {
    if (!quota || !asset || !value) return;

    let quotaObj: DbQuotaInfos = {
      platformId: cacheService.GetUserInfo()?.platformId ?? null,
      resourceId: asset.id,
      enabled: true,
      creationDate: new Date(),
      description: null,
      type: QuotaRulesEnum.AssetObjectTreeStorageSizeQuota,
      rule: new AssetObjectTreeStorageSizeQuota({ maxSizeInBytes: value * 1024 * 1024 * 1024 }),
      report: null,
      id: quota.id,
      isNew: false,
    };

    setupService.UpdateQuota(quotaObj).then((response) => {
      if (response.success) {
        enqueueCustomSnackbar(t("alert:success.quotaUpdated"), "success");
        setValue(0);
        setIsLoading(true);
        setTrigger((prevState) => !prevState);
      } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
    });
  }, [asset, value, quota]);

  const handleQuotaDelete = useCallback(() => {
    if (!quota) return;

    setupService.DeleteQuota(quota.id).then((response) => {
      if (response.success) {
        enqueueCustomSnackbar(t("alert:success.quotaRemoved"), "success");
        setQuota(undefined);
        setValue(0);
      } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
    });
  }, [quota]);

  return (
    <>
      {isLoading ? (
        <TableSkeleton height={150} />
      ) : (
        <Stack direction="column" justifyContent="space-between" alignItems="center" spacing={3}>
          <Stack direction="column" spacing={1} width={"100%"}>
            <Typography variant="h5" fontSize={"1rem"}>
              {quota ? t("alert:success.quotaEnabled") : t("page:body.quotaDisabled")}
            </Typography>
            <Stack direction="row" justifyContent="space-between" spacing={1} width={"100%"}>
              <Stack direction="column" spacing={0.5}>
                <Stack
                  alignItems={"center"}
                  direction="row"
                  justifyContent="space-between"
                  spacing={0.5}
                  width={"100%"}
                >
                  <Typography
                    color={
                      (quota?.report?.percentUsed ?? 0) < 80
                        ? "success.main"
                        : (quota?.report?.percentUsed ?? 0) >= 80 && (quota?.report?.percentUsed ?? 0) < 90
                        ? "warning.main"
                        : "error"
                    }
                  >
                    {quota?.report?.percentUsed ?? 0}%
                  </Typography>
                  <Box sx={(theme) => ({ width: "60px", flexShrink: 0, ml: `${theme.spacing(1)} !important` })}>
                    <LinearProgress
                      sx={{ borderRadius: "2px" }}
                      color={
                        (quota?.report?.percentUsed ?? 0) < 80
                          ? "success"
                          : (quota?.report?.percentUsed ?? 0) >= 80 && (quota?.report?.percentUsed ?? 0) < 90
                          ? "warning"
                          : "error"
                      }
                      variant="determinate"
                      value={quota?.report?.percentUsed ?? 0}
                    />
                  </Box>
                </Stack>
                {quota && quota.report && (
                  <Typography variant="body2" color={"text.tier"}>
                    {(quota.report!.valueUsed / 1024 / 1024 / 1024).toFixed(2)} /{" "}
                    {(quota.rule as AssetObjectTreeStorageSizeQuota).maxSizeInBytes / 1024 / 1024 / 1024} GB
                  </Typography>
                )}
              </Stack>
              {quota && (
                <Button variant="contained" color="error" startIcon={<ClearIcon />} onClick={() => handleQuotaDelete()}>
                  {t("common:remove")}
                </Button>
              )}
            </Stack>
          </Stack>
          <Divider sx={{ width: "100%" }} orientation="horizontal" />

          {aclUpdate && (
            <Stack direction="column" spacing={1.5} width={"100%"}>
              <Typography fontWeight={200}>{quota ? t("common:editUpperCase") : t("common:create")}</Typography>
              <Stack direction="row" justifyContent="space-between" spacing={1} width={"100%"}>
                <OutlinedInput
                  value={value}
                  fullWidth
                  onChange={(e) => setValue(parseInt(e.currentTarget.value))}
                  type="number"
                  placeholder={t("field:placeholder.maxSizeInGB")}
                />
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<BuildCircleIcon />}
                  onClick={() => {
                    quota ? handleQuotaUpdate() : handleQuotaCreate();
                  }}
                >
                  {quota ? t("common:update") : t("common:enable")}
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      )}
    </>
  );
};

export function CircularProgressWithLabel(props: CircularProgressProps & { value: number }) {
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress
        variant="determinate"
        {...props}
        color={props.value < 80 ? "success" : props.value >= 80 && props.value < 90 ? "warning" : "error"}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="caption" component="div" color="text.secondary">{`${Math.round(
          props.value
        )}%`}</Typography>
      </Box>
    </Box>
  );
}

export type AssetSingleQuotasProps = {
  fullwidthTable?: boolean;
  asset?: AssetPanelDto | undefined;
};

function AssetSingleQuotas(props: AssetSingleQuotasProps) {
  const currentObj = useSelector(GetObject);

  return (
    <Paper sx={{ backgroundImage: "none", p: 2, borderRadius: "6px", mt: 2 }}>
      <QuotaConfig asset={currentObj} />
    </Paper>
  );
}

export default AssetSingleQuotas;
